import { supabase } from '../lib/supabase';

export const familyService = {
  async createFamily(familyName, primaryAdminId) {
    const { data, error } = await supabase?.from('family_groups')?.insert({
        name: familyName,
        primary_admin_id: primaryAdminId
      })?.select()?.single();
    
    if (error) throw error;
    return data;
  },

  async getUserFamily() {
    const { data, error } = await supabase?.from('family_members')?.select(`
        *,
        family_groups (
          id,
          name,
          primary_admin_id
        )
      `)?.eq('user_id', (await supabase?.auth?.getUser())?.data?.user?.id)?.eq('is_active', true)?.single();
    
    if (error && error?.code !== 'PGRST116') throw error;
    return data;
  },

  async getFamilyMembers(familyGroupId) {
    const { data, error } = await supabase?.from('family_members')?.select(`
        *,
        user_profiles (
          email,
          full_name,
          avatar_url
        )
      `)?.eq('family_group_id', familyGroupId)?.eq('is_active', true)?.order('created_at', { ascending: true });
    
    if (error) throw error;
    return data;
  },

  async addFamilyMember(familyGroupId, memberData) {
    const { data, error } = await supabase?.from('family_members')?.insert({
        family_group_id: familyGroupId,
        user_id: memberData?.userId,
        display_name: memberData?.displayName,
        member_role: memberData?.memberRole,
        color: memberData?.color,
        initials: memberData?.initials,
        family_role: memberData?.familyRole || 'member'
      })?.select()?.single();
    
    if (error) throw error;
    return data;
  },

  async updateFamilyMember(memberId, updates) {
    const { data, error } = await supabase?.from('family_members')?.update({
        display_name: updates?.displayName,
        member_role: updates?.memberRole,
        color: updates?.color,
        initials: updates?.initials,
        updated_at: new Date()?.toISOString()
      })?.eq('id', memberId)?.select()?.single();
    
    if (error) throw error;
    return data;
  },

  async removeFamilyMember(memberId) {
    const { error } = await supabase?.from('family_members')?.update({ is_active: false })?.eq('id', memberId);
    
    if (error) throw error;
  },

  async inviteFamilyMember(familyGroupId, inviteeEmail, inviteeName, memberRole, color) {
    const { data: { user } } = await supabase?.auth?.getUser();
    
    const { data, error } = await supabase?.from('family_invitations')?.insert({
        family_group_id: familyGroupId,
        invited_by: user?.id,
        invitee_email: inviteeEmail,
        invitee_name: inviteeName,
        member_role: memberRole,
        color: color
      })?.select()?.single();
    
    if (error) throw error;
    return data;
  },

  async getPendingInvitations() {
    const { data: { user } } = await supabase?.auth?.getUser();
    const { data: profile } = await supabase?.from('user_profiles')?.select('email')?.eq('id', user?.id)?.single();
    
    const { data, error } = await supabase?.from('family_invitations')?.select(`
        *,
        family_groups (
          name
        ),
        user_profiles!family_invitations_invited_by_fkey (
          full_name
        )
      `)?.eq('invitee_email', profile?.email)?.eq('status', 'pending')?.gt('expires_at', new Date()?.toISOString());
    
    if (error) throw error;
    return data;
  },

  async acceptInvitation(invitationId) {
    const { data: invitation, error: invError } = await supabase?.from('family_invitations')?.select('*')?.eq('id', invitationId)?.single();
    
    if (invError) throw invError;
    
    const { data: { user } } = await supabase?.auth?.getUser();
    
    const initials = invitation?.invitee_name
      ? invitation?.invitee_name?.split(' ')?.map(n => n?.[0])?.join('')?.toUpperCase()?.substring(0, 2)
      : 'FM';
    
    const { error: memberError } = await supabase?.from('family_members')?.insert({
        family_group_id: invitation?.family_group_id,
        user_id: user?.id,
        display_name: invitation?.invitee_name || user?.email?.split('@')?.[0],
        member_role: invitation?.member_role,
        color: invitation?.color,
        initials: initials,
        family_role: 'member'
      });
    
    if (memberError) throw memberError;
    
    const { error: updateError } = await supabase?.from('family_invitations')?.update({
        status: 'accepted',
        accepted_at: new Date()?.toISOString()
      })?.eq('id', invitationId);
    
    if (updateError) throw updateError;
  },

  async getFamilyPreferences(familyGroupId) {
    const { data, error } = await supabase?.from('family_preferences')?.select('*')?.eq('family_group_id', familyGroupId)?.single();
    
    if (error) throw error;
    return data;
  },

  async updateFamilyPreferences(familyGroupId, preferences) {
    const { data, error } = await supabase?.from('family_preferences')?.update({
        ...preferences,
        updated_at: new Date()?.toISOString()
      })?.eq('family_group_id', familyGroupId)?.select()?.single();
    
    if (error) throw error;
    return data;
  }
};